import { SystemType, SystemTypeRaw } from '../types/system.types';
import systemTypesFile from '../schema/SystemTypes.yaml';
import { SystemTypeImpl } from './system.class';
import { Capability } from '../types/capabilities/capabilities.system.types';

/**
 * Returns the default system type.
 * @returns {Promise<string>} The default system type.
 */
export const getDefaultSystemType = async (): Promise<string> => {
	return 'WaterDistribution';
};

/**
 * Filter type for getting system types.
 */
type GetSystemTypeFilter = { capability: Capability };

/**
 * Converts a raw system type to a SystemType instance.
 * @internal
 * @param {SystemTypeRaw} raw - The raw system type.
 * @returns {SystemType} The SystemType instance.
 */
export const fromRawSystem = (raw: SystemTypeRaw): SystemType => {
	return new SystemTypeImpl(raw);
};

/**
 * Gets raw system types, optionally filtered by capability.
 * @internal
 * @param {GetSystemTypeFilter} [filter] - The filter to apply.
 * @returns {Promise<SystemTypeRaw[]>} The raw system types.
 */
export const getSystemTypesRaw = async (
	filter?: GetSystemTypeFilter,
): Promise<SystemTypeRaw[]> => {
	const systemTypes = systemTypesFile as unknown as SystemTypeRaw[];

	if (!filter) {
		return systemTypes;
	} else if ('capability' in filter) {
		return systemTypes.filter(s =>
			fromRawSystem(s).hasCapability(filter.capability),
		);
	}

	return [];
};

/**
 * Gets system types, optionally filtered by capability.
 * @param {GetSystemTypeFilter} [filter] - The filter to apply.
 * @returns {Promise<SystemType[]>} The system types.
 */
export const getSystemTypes = async (
	filter?: GetSystemTypeFilter,
): Promise<SystemType[]> => {
	return (await getSystemTypesRaw(filter)).map(fromRawSystem);
};

/**
 * Gets a raw system type by its name.
 * @internal
 * @param {string} systemType - The name of the system type.
 * @returns {Promise<SystemTypeRaw | undefined>} The raw system type, or undefined if not found.
 */
export const getSystemTypeRaw = async (
	systemType: string,
): Promise<SystemTypeRaw | undefined> => {
	const systemTypes = await getSystemTypesRaw();

	return systemTypes.find(s => s.SystemType === systemType);
};

/**
 * Gets a system type by its name.
 * @param {string} systemType - The name of the system type.
 * @returns {Promise<SystemType | undefined>} The system type, or undefined if not found.
 */
export const getSystemType = async (
	systemType: string,
): Promise<SystemType | undefined> => {
	const raw = await getSystemTypeRaw(systemType);

	if (raw == undefined) {
		return undefined;
	}

	return fromRawSystem(raw);
};

/**
 * Gets a raw system type by an asset type.
 * @internal
 * @param {string} assetType - The asset type.
 * @returns {Promise<SystemTypeRaw | undefined>} The raw system type, or undefined if not found.
 */
export const getSystemTypeByAssetTypeRaw = async (
	assetType: string,
): Promise<SystemTypeRaw | undefined> => {
	const systemTypes = await getSystemTypesRaw();

	return systemTypes.find(s => s.AssetTypes.includes(assetType));
};

/**
 * Gets a system type by an asset type.
 * @param {string} assetType - The asset type.
 * @returns {Promise<SystemType | undefined>} The system type, or undefined if not found.
 */
export const getSystemTypeByAssetType = async (
	assetType: string,
): Promise<SystemType | undefined> => {
	const raw = await getSystemTypeByAssetTypeRaw(assetType);

	if (raw == undefined) {
		return undefined;
	}

	return fromRawSystem(raw);
};

const SYSTEM_TYPE_MAPPING = {
	ww: 'SanitarySewer',
	sw: 'Stormwater',
	cb: 'CombinedSewer',
	default: 'WaterDistribution',
} as const satisfies Record<string, string>;

type SystemTypeMapping = typeof SYSTEM_TYPE_MAPPING;
type SystemTypePrefix = Exclude<keyof SystemTypeMapping, 'default'>;

/**
 * Gets the system type prefixes.
 * @returns {SystemTypePrefix[]} The system type prefixes.
 */
export function getSystemTypePrefixes(): SystemTypePrefix[] {
	return Object.keys(SYSTEM_TYPE_MAPPING).filter(
		(prefix): prefix is SystemTypePrefix => prefix !== 'default',
	);
}

/**
 * Gets the system type ID by an asset type prefix.
 * @param {string} assetType - The asset type.
 * @returns {SystemTypeMapping[keyof SystemTypeMapping]} The system type ID.
 */
export function getSystemTypeIdByAssetTypePrefix(
	assetType: string,
): SystemTypeMapping[keyof SystemTypeMapping] {
	const systemTypePrefixes = getSystemTypePrefixes();
	const prefix =
		systemTypePrefixes.find(prefix => assetType.startsWith(prefix)) ||
		'default';

	return SYSTEM_TYPE_MAPPING[prefix];
}

/**
 * Gets the asset type prefix by a system type.
 * @param {string} systemType - The system type.
 * @returns {SystemTypePrefix | ''} The asset type prefix, or an empty string if not found.
 */
export function getAssetTypePrefixBySystemType(
	systemType: string,
): SystemTypePrefix | '' {
	const systemTypePrefixes = getSystemTypePrefixes();
	const prefix =
		systemTypePrefixes.find(
			prefix => getSystemTypeIdByAssetTypePrefix(prefix) === systemType,
		) || '';

	return prefix;
}
